<template>
  <div class="chart-wrapper reasons-chart">
    <template v-if="loading">
      <h3 class="chart-title">{{ chartTitle }}</h3>
      <div class="reasons-chart__skeleton">
        <div class="reasons-chart__skeleton-circle" />
        <div class="reasons-chart__skeleton-lines">
          <div
            v-for="n in 5"
            :key="n"
            class="reasons-chart__skeleton-line"
          />
        </div>
      </div>
    </template>
    <template v-else-if="!hasData">
      <h3 class="chart-title">{{ chartTitle }}</h3>
      <bi-chart-empty />
    </template>
    <top5-list
      v-else-if="mode === 'list'"
      :data="listData"
      :columns="listColumns"
      :title="chartTitle"
    />
    <template v-else>
      <h3 class="chart-title">{{ chartTitle }}</h3>
      <div class="reasons-chart__content">
        <div class="reasons-chart__chart-area">
          <v-chart
            ref="chart"
            class="reasons-chart__chart"
            :option="option"
            autoresize
            @click="onSliceClick"
            @mouseover="onSliceHover"
            @mouseout="onSliceOut"
          />
        </div>
        <div class="reasons-chart__legend">
          <div class="reasons-chart__legend-header">
            <span>Motivo</span>
            <span>Total</span>
          </div>
          <div
            v-for="(item, i) in data"
            :key="i"
            class="reasons-chart__legend-row"
            :class="{
              'reasons-chart__legend-row--active': activeLegendIndex === i,
            }"
            @mouseenter="onLegendEnter(item, i)"
            @mouseleave="onLegendLeave"
            @click="onLegendClick(item, i)"
          >
            <div class="reasons-chart__legend-name">
              <span
                class="reasons-chart__dot"
                :style="{ background: getColorByCause(item.cause) }"
              />
              {{ i + 1 }}. {{ item.name }}
            </div>
            <span class="reasons-chart__total">{{ formatCount(item.value) }}</span>
          </div>
        </div>
      </div>
      <div class="reasons-chart__footer">
        <span class="reasons-chart__footer-item">
          <span
            class="reasons-chart__dot"
            :style="{ background: clientColor }"
          />
          Causa Cliente ({{ clientCausePercent }}%)
        </span>
        <span class="reasons-chart__footer-item">
          <span
            class="reasons-chart__dot"
            :style="{ background: supportColor }"
          />
          Causa Apoio ({{ 100 - clientCausePercent }}%)
        </span>
      </div>
    </template>
  </div>
</template>

<script>
import VChart from 'vue-echarts'
import Top5List from './Top5List.vue'
import BiChartEmpty from './BiChartEmpty.vue'
import { hasChartData } from './chartData'

export default {
  name: 'ReasonsChart',
  components: { VChart, Top5List, BiChartEmpty },
  props: {
    title: { type: String, default: '' },
    loading: { type: Boolean, default: false },
    mode: {
      type: String,
      default: 'list',
      validator: v => ['donut', 'list'].includes(v),
    },
    data: {
      type: Array,
      default: () => [],
    },
    clientCausePercent: { type: Number, default: null },
    centerValue: { type: String, default: '--' },
    centerLabel: { type: String, default: 'Cliente' },
  },
  data() {
    return {
      clientColor: '#5B9BD5',
      supportColor: '#E8A0B8',
      hoveredCause: null,
      hoveredIndex: null,
      selectedCause: null,
      selectedIndex: null,
    }
  },
  computed: {
    hasData() {
      return (
        hasChartData(this.data, 'value') &&
        (this.mode !== 'donut' || Number.isFinite(this.clientCausePercent))
      )
    },
    activeLegendIndex() {
      return this.hoveredIndex === null ? this.selectedIndex : this.hoveredIndex
    },
    activeCause() {
      return this.hoveredCause || this.selectedCause
    },
    total() {
      return this.data.reduce((s, d) => s + d.value, 0)
    },
    donutData() {
      const clientTotal = Math.round((this.clientCausePercent / 100) * this.total)
      return [
        {
          name: 'Causa Cliente',
          value: clientTotal,
          itemStyle: { color: this.clientColor, borderRadius: 3 },
        },
        {
          name: 'Causa Apoio',
          value: this.total - clientTotal,
          itemStyle: { color: this.supportColor, borderRadius: 3 },
        },
      ]
    },
    option() {
      return {
        tooltip: {
          trigger: 'item',
          formatter: ({ name, value, percent }) =>
            `<b>${name}</b><br/>Total: <b>${this.formatCount(value)}</b><br/>${percent.toFixed(2).replace('.', ',')}%`,
        },
        graphic: [
          {
            type: 'text',
            left: 'center',
            top: '38%',
            style: {
              text: `${this.centerValue}\n${this.centerLabel}`,
              textAlign: 'center',
              fill: '#333',
              fontSize: 12,
              fontWeight: 'bold',
              lineHeight: 22,
            },
          },
        ],
        series: [
          {
            type: 'pie',
            radius: ['42%', '70%'],
            center: ['50%', '50%'],
            avoidLabelOverlap: false,
            label: { show: false },
            emphasis: {
              scale: true,
              scaleSize: 6,
              label: { show: true, fontSize: 12, fontWeight: 'bold' },
              itemStyle: {
                shadowBlur: 10,
                shadowColor: 'rgba(80, 80, 80, 0.18)',
              },
            },
            data: this.donutData,
          },
        ],
      }
    },
    chartTitle() {
      if (this.title) return this.title
      return this.mode === 'donut' ? 'Motivos de Recoletas' : 'Top 5 Motivos de Recoletas'
    },
    listData() {
      return this.data.map(item => ({
        ...item,
        displayValue: this.formatCount(item.value),
      }))
    },
    listColumns() {
      return [
        { key: 'name', title: 'Motivo', indexPrefix: true },
        { key: 'displayValue', title: 'Total', align: 'right' },
      ]
    },
  },
  methods: {
    formatCount(value) {
      const numericValue = Number(value)
      if (
        value === null ||
        value === undefined ||
        value === '' ||
        !Number.isFinite(numericValue)
      )
        return '--'
      return numericValue.toLocaleString('pt-BR')
    },
    getChart() {
      const chart = this.$refs.chart
      if (!chart) return null
      if (typeof chart.dispatchAction === 'function') return chart
      if (typeof chart.getEchartsInstance === 'function')
        return chart.getEchartsInstance()
      return chart.chart || null
    },
    syncHighlight(cause) {
      const chart = this.getChart()
      if (!chart) return

      chart.dispatchAction({ type: 'downplay', seriesIndex: 0 })

      if (!cause) {
        chart.dispatchAction({ type: 'hideTip' })
        return
      }

      const index = cause === 'support' ? 1 : 0

      chart.dispatchAction({ type: 'highlight', seriesIndex: 0, dataIndex: index })
      chart.dispatchAction({ type: 'showTip', seriesIndex: 0, dataIndex: index })
    },
    getCauseFromItem(item) {
      return item && item.cause === 'support' ? 'support' : 'client'
    },
    getColorByCause(cause) {
      return cause === 'support' ? this.supportColor : this.clientColor
    },
    onLegendEnter(item, index) {
      this.hoveredIndex = index
      this.hoveredCause = this.getCauseFromItem(item)
      this.syncHighlight(this.hoveredCause)
    },
    onLegendLeave() {
      this.hoveredIndex = null
      this.hoveredCause = null
      this.syncHighlight(this.selectedCause)
    },
    onLegendClick(item, index) {
      this.selectedIndex = index
      this.selectedCause = this.getCauseFromItem(item)
      this.hoveredIndex = null
      this.hoveredCause = null
      this.syncHighlight(this.selectedCause)
      this.$emit('slice-click', { name: item.name, value: item.value })
    },
    onSliceHover(params) {
      if (params.componentType !== 'series') return
      this.hoveredCause = params.dataIndex === 1 ? 'support' : 'client'
    },
    onSliceOut() {
      this.hoveredCause = null
    },
    onSliceClick(params) {
      this.selectedCause = params.dataIndex === 1 ? 'support' : 'client'
      this.syncHighlight(this.selectedCause)
      this.$emit('slice-click', { name: params.name, value: params.value })
    },
  },
}
</script>

<style scoped lang="scss">
.chart-wrapper {
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
  flex: 1;
  box-sizing: border-box;
  font-family: 'Roboto', sans-serif;
}

.chart-title {
  font-size: 16px;
  font-weight: 700;
  color: rgba(70, 70, 70, 1);
  margin: 0 0 12px;
}

@keyframes bi-shimmer {
  0% {
    opacity: 0.4;
  }
  50% {
    opacity: 0.9;
  }
  100% {
    opacity: 0.4;
  }
}

.reasons-chart {
  &__skeleton {
    display: flex;
    gap: 16px;
    align-items: center;
    padding: 8px 0;
  }

  &__skeleton-circle {
    flex-shrink: 0;
    width: 120px;
    height: 120px;
    border-radius: 50%;
    background: #e9e9e9;
    animation: bi-shimmer 1.4s ease-in-out infinite;
  }

  &__skeleton-lines {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__skeleton-line {
    height: 14px;
    border-radius: 2px;
    background: #e9e9e9;
    animation: bi-shimmer 1.4s ease-in-out infinite;

    &:nth-child(1) {
      width: 90%;
    }
    &:nth-child(2) {
      width: 75%;
    }
    &:nth-child(3) {
      width: 85%;
    }
    &:nth-child(4) {
      width: 60%;
    }
    &:nth-child(5) {
      width: 70%;
    }
  }

  &__content {
    display: flex;
    gap: 16px;
    align-items: center;
    margin-left: 0;
    padding-left: 0;
  }

  &__chart-area {
    flex: 0 0 160px;
    margin-left: 0;
    padding-left: 0;
  }

  &__chart {
    width: 160px;
    height: 160px;
  }

  &__legend {
    flex: 1;
    min-width: 0;
  }

  &__legend-header {
    display: flex;
    justify-content: space-between;
    font-size: 14px;
    color: rgba(90, 90, 90, 1);
    padding-bottom: 4px;
    margin-bottom: 4px;
  }

  &__legend-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 0;
    font-size: 14px;
    color: #444;
    border-bottom: 1px solid rgba(230, 230, 230, 1);
    border-radius: 0;
    cursor: pointer;
    transition: background-color 120ms ease;

    &--active,
    &:hover {
      background-color: rgba(242, 242, 242, 1);
    }
  }

  &__legend-name {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__total {
    color: #333;
    margin-left: 8px;
    flex-shrink: 0;
    font-size: 14px;
  }

  &__dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  &__footer {
    display: flex;
    gap: 20px;
    margin-top: 12px;
    padding-top: 10px;
  }

  &__footer-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #555;
  }
}
</style>
